import { 
  LiveKitRoom, 
  VideoConference, 
  useConnectionState,
  useParticipants,
  useLocalParticipant,
  ControlBar,

  DisconnectButton
} from '@livekit/components-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TypedLiveKitRoom, TypedDisconnectButton } from './utils/livekitTyped';
import { Loading } from '../shared/Loading';

interface TempleLiveKitPatientMeetingProps {
  appointmentId: string;
  liveKitResponse: any;
  setJoinCall: (value: boolean) => void;
  providerName: string;
}

export function TempleLiveKitPatientMeeting({
  appointmentId,
  liveKitResponse,
  setJoinCall,
  providerName
}: TempleLiveKitPatientMeetingProps) {
  const [callDuration, setCallDuration] = useState<string>('00:00:00');
  const [showInfo, setShowInfo] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  const navigate = useNavigate();

  // Handle meeting end
  const handleMeetingEnd = () => {
    setJoinCall(false);
    navigate('/patient/dashboard');
  };

  if (!liveKitResponse?.token || !liveKitResponse?.livekitUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-gray-50">
      <TypedLiveKitRoom
        token={liveKitResponse.token}
        serverUrl={liveKitResponse.livekitUrl}
        connect={true}
        onDisconnected={handleMeetingEnd}
        audio={true}
        video={true}
      >
        <MeetingContent 
          appointmentId={appointmentId}
          providerName={providerName}
          callDuration={callDuration}
          setCallDuration={setCallDuration}
          showInfo={showInfo}
          setShowInfo={setShowInfo}
          startTime={startTime}
          setStartTime={setStartTime}
          handleMeetingEnd={handleMeetingEnd}
        />
      </TypedLiveKitRoom>
    </div>
  );
}

function MeetingContent({
  appointmentId,
  providerName,
  callDuration,
  setCallDuration,
  showInfo,
  setShowInfo,
  startTime,
  setStartTime,
  handleMeetingEnd
}: any) {
  const connectionState = useConnectionState();
  const localParticipant = useLocalParticipant();
  const participants = useParticipants();

  // Format call duration
  const formatDuration = (startTime: Date) => {
    const now = new Date();
    const diff = now.getTime() - startTime.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Start call duration timer
  useEffect(() => {
    if (connectionState === 'connected' && !startTime) {
      setStartTime(new Date());
    }

    if (startTime) {
      const interval = setInterval(() => {
        setCallDuration(formatDuration(startTime));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [connectionState, startTime]);

  return (
    <div className="flex flex-col h-screen">
      {/* Header with call info */}
      <div className="bg-blue-50 text-gray-800 p-4 flex justify-between items-center border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-800">Meeting with {providerName}</h2>
          <span className="text-sm text-gray-600">{callDuration}</span>
          <span className="text-sm text-green-600">
            {connectionState === 'connected' ? 'Connected' : 'Connecting...'}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            className="relative p-2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            {showInfo && (
              <div className="absolute right-0 top-8 w-48 p-2 bg-white border border-gray-200 text-gray-700 text-xs rounded shadow-lg z-10">
                <p>Meeting ID: {appointmentId}</p>
                <p>Participants: {participants.length}</p>
                <p>Connection: {connectionState}</p>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Video conference area */}
      <div className="flex-1 relative">
        <VideoConference />
      </div>

      {/* Control bar */}
      {/* <div className="bg-white border-t border-gray-200 p-4">
        <ControlBar>
          
          <TypedDisconnectButton />
        </ControlBar>
      </div> */}
    </div>
  );
}
